import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { JobRole, JobPosition } from "../../types";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Plus, Briefcase, Info, MoreVertical, Search, Filter, MapPin, User, Calendar, Clock, Eye, Edit, Trash2, RefreshCw, Copy } from "lucide-react";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import { toSydneyDate } from "../../utils/helpers";

interface JobPositionsTabProps {
  jobRole: JobRole;
  isActive: boolean;
  shouldOpenForm?: boolean;
  onFormOpened?: () => void;
  onPositionsChange?: () => void;
}

export const JobPositionsTab: React.FC<JobPositionsTabProps> = ({ jobRole, isActive, shouldOpenForm, onFormOpened, onPositionsChange }) => {
  const navigate = useNavigate();
  const [selectedPosition, setSelectedPosition] = useState<JobPosition | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState<JobPosition | null>(null);
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/${jobRole.id}/positions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setPositions(data.positions);
      }
    } catch (error) {
      console.error("Error fetching positions:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isActive) {
      fetchPositions();
    }
  }, [isActive, jobRole.id]);

  React.useEffect(() => {
    if (isActive && shouldOpenForm) {
      navigate(`/job-roles/${jobRole.id}/positions/new`);
      onFormOpened?.();
    }
  }, [isActive, shouldOpenForm, jobRole.id, navigate, onFormOpened]);

  const handleViewDetails = (position: JobPosition) => {
    setSelectedPosition(position);
    setIsDetailsModalOpen(true);
  };

  const handleEditPosition = (position: JobPosition) => {
    navigate(`/job-roles/${jobRole.id}/positions/${position.id}/edit`);
  };

  const handleCopyPosition = (position: JobPosition) => {
    navigate(`/job-roles/${jobRole.id}/positions/${position.id}/copy`);
  };

  const handleDeletePosition = async () => {
    if (!positionToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/${jobRole.id}/positions/${positionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Job position deleted successfully");
        setIsDeleteModalOpen(false);
        setPositionToDelete(null);
        fetchPositions();
        onPositionsChange?.();
      } else {
        toast.error(data.message || "Failed to delete job position");
      }
    } catch (error) {
      console.error("Error deleting job position:", error);
      toast.error("An error occurred while deleting the position");
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Filled':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-none">Filled</Badge>;
      case 'Vacant':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-none">Vacant</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Job Positions</h2>
          <p className="text-sm text-muted-foreground">
            {positions.length} positions for <span className="font-medium text-foreground">{jobRole.title}</span> role
          </p>
        </div>
        <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-2 px-6" onClick={() => {
          navigate(`/job-roles/${jobRole.id}/positions/new`);
        }}>
          <Plus size={16} />
          <span>Add Position</span>
        </Button>
      </div>

      <Card className="border shadow-none overflow-hidden">
        <div className="p-4 border-b bg-gray-50/50 flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search positions..."
              className="pl-10 bg-white"
            />
          </div>
          <Button variant="outline" size="sm" className="bg-white flex items-center gap-2">
            <Filter size={16} />
            <span>Filter</span>
          </Button>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
              <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
              <span>Loading data...</span>
            </div>
          )}
        </div>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Position Code</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Location</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Reports To</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Employment Type</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Start Date</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Status</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500">Occupied By</TableHead>
                <TableHead className="font-bold text-xs uppercase text-gray-500 ">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {positions.length > 0 ? (
                positions.map((position) => (
                  <TableRow key={position.id} className="hover:bg-gray-50/50 cursor-pointer" onClick={() => handleViewDetails(position)}>
                    <TableCell className="font-medium">{position.positionCode}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <MapPin size={14} className="text-gray-400" />
                        {position.location}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{position.reportsTo?.name || "N/A"}</TableCell>
                    <TableCell className="text-sm text-gray-600">{position.employmentType}</TableCell>
                    <TableCell className="text-sm text-gray-600">{toSydneyDate(position.startDate) || "N/A"}</TableCell>
                    <TableCell>
                      <div className="flex uppercase">
                        {getStatusBadge(position.status)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600" title={position.assignedUser?.name || ""}>
                      {position.assignedUser?.name || ""}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={() => handleViewDetails(position)}>
                          <Eye size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={(e) => {
                          e.stopPropagation();
                          handleEditPosition(position);
                        }}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-green-600" onClick={(e) => {
                          e.stopPropagation();
                          handleCopyPosition(position);
                        }}>
                          <Copy size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-red-600" onClick={(e) => {
                          e.stopPropagation();
                          setPositionToDelete(position);
                          setIsDeleteModalOpen(true);
                        }}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="h-64 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="bg-gray-100 p-4 rounded-full">
                        <Briefcase className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium">No positions found</p>
                        <p className="text-sm text-muted-foreground">Add a job position to get started.</p>
                      </div>
                      <Button variant="outline" size="sm" className="mt-4" onClick={() => {
                        navigate(`/job-roles/${jobRole.id}/positions/new`);
                      }}>
                        <Plus size={16} className="mr-2" /> ADD POSITION
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Position Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span className="text-xl font-bold">{selectedPosition?.positionCode} - Details</span>
              {selectedPosition && getStatusBadge(selectedPosition.status)}
            </DialogTitle>
            <DialogDescription>
              Detailed information for this specific job position.
            </DialogDescription>
          </DialogHeader>

          {selectedPosition && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Department</h4>
                    <p className="text-sm font-medium">{jobRole.department?.departmentCode} - {jobRole.department?.departmentName}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Location</h4>
                    <div className="flex items-center gap-1.5 text-sm">
                      <MapPin size={14} className="text-gray-400" />
                      {selectedPosition.location}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Reports To</h4>
                    <div className="flex items-center gap-1.5 text-sm">
                      <User size={14} className="text-gray-400" />
                      {selectedPosition.reportsTo?.name || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Employment Type</h4>
                    <div className="flex items-center gap-1.5 text-sm">
                      <Clock size={14} className="text-gray-400" />
                      {selectedPosition.employmentType}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Start Date</h4>
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar size={14} className="text-gray-400" />
                      {toSydneyDate(selectedPosition.startDate) || "N/A"}
                    </div>
                  </div>
                  {selectedPosition.endDate && (
                    <div>
                      <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">End Date</h4>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar size={14} className="text-gray-400" />
                        {toSydneyDate(selectedPosition.endDate) || "N/A"}
                      </div>
                    </div>
                  )}
                  <div>
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-1">Occupied By</h4>
                    <div className="flex items-center gap-1.5 text-sm">
                      <User size={14} className="text-gray-400" />
                      {selectedPosition.assignedUser?.name || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="text-xs font-bold uppercase text-gray-500 mb-2">Business Justification</h4>
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <p className="text-sm leading-relaxed text-gray-700 italic">
                    {selectedPosition.businessJustification}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <Info size={16} className="text-blue-600 shrink-0" />
                <p className="text-xs text-blue-700">
                  This position inherits the core duties and requirements defined in the <span className="font-bold">{jobRole.title}</span> role.
                </p>
              </div>
              <DialogFooter className="sm:justify-end gap-2">
                <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>Close</Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleEditPosition(selectedPosition)}>Edit Position</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Info size={20} />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Are you sure you want to delete position <span className="font-bold text-gray-900">{positionToDelete?.positionCode}</span>?
            </DialogDescription>
          </DialogHeader>

          <div className="bg-amber-50 p-3 border border-amber-100 rounded-lg text-xs text-amber-800 space-y-2">
            <p className="font-bold uppercase flex items-center gap-1.5">
              <Info size={14} /> Historical Data Preservation
            </p>
            <p className="leading-relaxed">
              This action will remove the position from active lists. However, all historical records, activity logs, and previous assignments related to this position will be preserved in the archive for auditing purposes.
            </p>
          </div>

          <DialogFooter className="flex sm:justify-between items-center gap-2 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={deleting}
              className="text-gray-500 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeletePosition}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 flex items-center gap-2"
            >
              {deleting ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={14} />
                  Delete Position
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
